import { Lock } from 'lucide-react';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import type { BenchMandatorySetView } from '@siliconx/shared';
import {
  addStep,
  addableKinds,
  areaLabel,
  isDestructiveKind,
  kindLabel,
  mandatoryCondition,
  orderAreas,
  setScope,
  toggleStep,
  type PlanDraft,
} from './planModel';

/**
 * SIL-2246 U1. Plan builder as area cards. One card per area: header with an "in scope" checkbox (inventory is always on),
 * locked rows for the LIVE SiliconX mandatory set (lock icon, "SiliconX" tag, condition), the org's own rows with a checkbox,
 * and "+ Add test" limited to kinds an org may add. Out-of-scope cards are collapsed and dimmed. Controlled: the route owns the
 * draft. Read-only for a member (and for SiliconX templates). Every rule is re-checked by the save definer; this is UX.
 */
export function PlanBuilder({
  draft,
  mandatory,
  readOnly,
  onChange,
}: {
  draft: PlanDraft;
  mandatory: BenchMandatorySetView | null;
  readOnly: boolean;
  onChange: (next: PlanDraft) => void;
}) {
  const areas = orderAreas([...Object.keys(draft.scope), ...(mandatory?.items.map((i) => i.area) ?? [])]);
  return (
    <div className="space-y-3" data-testid="plan-builder">
      {areas.map((area) => {
        const inScope = area === 'inventory' || draft.scope[area] !== false;
        const locked = (mandatory?.items ?? []).filter((i) => i.area === area);
        const own = draft.steps.filter((s) => s.area === area);
        // A kind SiliconX already requires here needs no org copy.
        const addable = addableKinds(area).filter(
          (k) => !own.some((s) => s.stepKind === k && s.on) && !locked.some((i) => i.stepKind === k),
        );
        const headingId = `plan-area-${area}`;
        return (
          <section
            key={area}
            aria-labelledby={headingId}
            data-testid={`plan-area-${area}`}
            className={`rounded-xl border border-border bg-card p-4 shadow-sm ${inScope ? '' : 'opacity-60'}`}
          >
            <div className="flex items-center justify-between gap-3">
              <h3 id={headingId} className="text-[15px] font-semibold">{areaLabel(area)}</h3>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={inScope}
                  disabled={readOnly || area === 'inventory'}
                  onChange={(e) => onChange(setScope(draft, area, e.target.checked))}
                />
                In scope
                {area === 'inventory' && <span className="text-xs text-muted-foreground">(always on)</span>}
              </label>
            </div>

            {!inScope ? (
              <p className="mt-2 text-xs text-muted-foreground">
                Out of scope: these parts are recorded in the inventory but not tested, graded or turned into assets.
              </p>
            ) : (
              <div className="mt-3 space-y-1.5">
                {locked.length === 0 && own.length === 0 && (
                  <p className="text-xs text-muted-foreground">No tests in this area.</p>
                )}
                {locked.map((i) => (
                  <div key={`${i.area}:${i.stepKind}`} className="flex flex-wrap items-center gap-2 text-sm">
                    <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                    <span>{kindLabel(i.stepKind)}</span>
                    <StatusPill tone="neutral" title="Required by SiliconX. Cannot be removed or weakened.">SiliconX</StatusPill>
                    <span className="text-xs text-muted-foreground">{mandatoryCondition(i.area)}</span>
                  </div>
                ))}
                {own.map((s) => (
                  <label key={`${s.area}:${s.stepKind}`} className="flex flex-wrap items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={s.on}
                      disabled={readOnly}
                      onChange={(e) => onChange(toggleStep(draft, s.area, s.stepKind, e.target.checked))}
                    />
                    <span className={s.on ? '' : 'text-muted-foreground line-through'}>{kindLabel(s.stepKind)}</span>
                    {isDestructiveKind(s.stepKind) && <StatusPill tone="red">Wipes data</StatusPill>}
                  </label>
                ))}
                {!readOnly && addable.length > 0 && (
                  <div className="pt-1">
                    <label className="sr-only" htmlFor={`add-${area}`}>Add a test to {areaLabel(area)}</label>
                    <select
                      id={`add-${area}`}
                      value=""
                      onChange={(e) => {
                        if (e.target.value) onChange(addStep(draft, area, e.target.value));
                      }}
                      className="rounded-md border border-border bg-background px-2 py-1 text-sm"
                    >
                      <option value="">+ Add test</option>
                      {addable.map((k) => (
                        <option key={k} value={k}>{kindLabel(k)}{isDestructiveKind(k) ? ' (wipes data)' : ''}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
